'use client';

import * as React from 'react';
import { ArrowDown01Icon, ArrowUp01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

export const SCROLL_PANE_EDGE_THRESHOLD = 8;
export const SCROLL_PANE_STEP_RATIO = 0.85;

/** Shared list scroll surface — hides native scrollbars. */
export const scrollPaneListClass =
  'scrollbar-none overflow-y-auto overscroll-y-auto [scrollbar-width:none]';

const WHEEL_SCROLL_LOCK_SELECTOR =
  'textarea, input, select, [contenteditable="true"], [role="listbox"], [role="dialog"], [data-radix-popper-content-wrapper], [data-wheel-scroll-lock]';

/** True when wheel events should scroll the focused control instead of a pane. */
export function isWheelScrollLocked(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return !!target.closest(WHEEL_SCROLL_LOCK_SELECTOR);
}

function overflowYAllowsScroll(value: string): boolean {
  return value === 'auto' || value === 'scroll' || value === 'overlay';
}

function isOverflowScrollable(element: HTMLElement): boolean {
  const { overflowY } = getComputedStyle(element);
  if (!overflowYAllowsScroll(overflowY)) return false;
  return element.scrollHeight > element.clientHeight + SCROLL_PANE_EDGE_THRESHOLD;
}

function canScrollY(element: HTMLElement, delta: number): boolean {
  if (!isOverflowScrollable(element)) return false;

  const maxScroll = element.scrollHeight - element.clientHeight;
  if (delta > 0) return element.scrollTop < maxScroll - SCROLL_PANE_EDGE_THRESHOLD;
  if (delta < 0) return element.scrollTop > SCROLL_PANE_EDGE_THRESHOLD;
  return false;
}

/** Nearest scroll surface from an event target, if any. */
export function findNearestScrollPane(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null;
  const pane = target.closest('[data-scroll-pane]');
  return pane instanceof HTMLElement ? pane : null;
}

/** First scroll pane inside a container (fallback for delegated wheel scroll). */
export function findScrollPaneInContainer(container: HTMLElement): HTMLElement | null {
  const pane = container.querySelector('[data-scroll-pane]');
  return pane instanceof HTMLElement ? pane : null;
}

/** Walk up the DOM and return the innermost scrollable ancestor for this wheel delta. */
export function findScrollableAncestor(start: EventTarget | null, delta: number): HTMLElement | null {
  if (!(start instanceof HTMLElement) || delta === 0) return null;

  let node: HTMLElement | null = start;
  while (node && node !== document.documentElement) {
    if (canScrollY(node, delta)) return node;
    node = node.parentElement;
  }

  return null;
}

export function applyWheelScroll(element: HTMLElement, delta: number): boolean {
  if (!canScrollY(element, delta)) return false;
  element.scrollTop += delta;
  return true;
}

/**
 * Document-level wheel handler — scrolls the nearest overflow container and only
 * prevents default when scrolling actually happens (or an outer container can scroll).
 */
export function handleGlobalWheelEvent(_event: WheelEvent): void {
  // Intentionally empty — rely on native wheel scrolling on overflow containers.
}

export type UseScrollPaneWheelOptions = {
  enabled?: boolean;
  /** Scroll this pane when the pointer is not already over a pane. */
  defaultScrollRef?: React.RefObject<HTMLElement | null>;
};

/**
 * @deprecated Native wheel scrolling is used — this hook is a no-op.
 */
export function useScrollPaneWheel(
  _containerRef: React.RefObject<HTMLElement | null>,
  _options: UseScrollPaneWheelOptions = {},
) {
  // No-op — see GlobalScrollWheel.
}

type ScrollDirection = 'down' | 'up';

function resolveScrollDirection(scrollTop: number, scrollHeight: number, clientHeight: number): ScrollDirection | null {
  const scrollable = scrollHeight > clientHeight + SCROLL_PANE_EDGE_THRESHOLD;
  if (!scrollable) return null;

  const atTop = scrollTop <= SCROLL_PANE_EDGE_THRESHOLD;
  const atBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_PANE_EDGE_THRESHOLD;

  if (atTop) return atBottom ? null : 'down';
  if (atBottom) return 'up';

  const maxScroll = scrollHeight - clientHeight;
  return scrollTop < maxScroll / 2 ? 'down' : 'up';
}

function scrollPaneByStep(element: HTMLElement, direction: ScrollDirection) {
  const distance = Math.max(element.clientHeight * SCROLL_PANE_STEP_RATIO, 120);
  element.scrollBy({
    top: direction === 'down' ? distance : -distance,
    behavior: 'smooth',
  });
}

export function useFloatingScrollDirection(
  scrollRef: React.RefObject<HTMLElement | null>,
  contentKey?: string | number,
) {
  const [direction, setDirection] = React.useState<ScrollDirection | null>(null);
  const frameRef = React.useRef<number | null>(null);

  const updateDirection = React.useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      setDirection(null);
      return;
    }

    if (frameRef.current != null) return;

    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      const node = scrollRef.current;
      if (!node) return;
      const { scrollTop, scrollHeight, clientHeight } = node;
      const next = resolveScrollDirection(scrollTop, scrollHeight, clientHeight);
      setDirection((prev) => (prev === next ? prev : next));
    });
  }, [scrollRef]);

  React.useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    updateDirection();
    element.addEventListener('scroll', updateDirection, { passive: true });

    const resizeObserver = new ResizeObserver(updateDirection);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener('scroll', updateDirection);
      resizeObserver.disconnect();
      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [updateDirection, contentKey, scrollRef]);

  const scrollByStep = React.useCallback(() => {
    const element = scrollRef.current;
    if (!element || !direction) return;
    scrollPaneByStep(element, direction);
  }, [direction, scrollRef]);

  return { direction, scrollByStep };
}

export type FloatingScrollControlProps = {
  scrollRef: React.RefObject<HTMLElement | null>;
  contentKey?: string | number;
  className?: string;
};

/** Floating up/down control for an existing scroll container. */
export function FloatingScrollControl({ scrollRef, contentKey, className }: FloatingScrollControlProps) {
  const { direction, scrollByStep } = useFloatingScrollDirection(scrollRef, contentKey);

  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center transition-opacity duration-200',
        direction ? 'opacity-100' : 'opacity-0',
        className,
      )}
      aria-hidden={!direction}
    >
      <Button
        type="button"
        variant="iconAction"
        size="icon-action"
        className={cn(
          'pointer-events-auto size-10 rounded-full border border-border bg-background/95 shadow-md backdrop-blur-sm',
          !direction && 'pointer-events-none',
        )}
        aria-label={direction === 'down' ? 'Scroll down' : 'Scroll up'}
        onClick={scrollByStep}
      >
        <HugeiconsIcon
          icon={direction === 'down' ? ArrowDown01Icon : ArrowUp01Icon}
          size={20}
          strokeWidth={1.75}
        />
      </Button>
    </div>
  );
}

export type ScrollPaneProps = {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
  contentKey?: string | number;
  /** Show centered floating scroll button (default: true). */
  floatingControl?: boolean;
  /** Stretch to fill a flex parent (default: true). */
  fill?: boolean;
};

/** Default scrollable list surface — hidden scrollbar + optional floating scroll control. */
export const ScrollPane = React.forwardRef<HTMLDivElement, ScrollPaneProps>(function ScrollPane(
  { children, className, containerClassName, containerStyle, contentKey, floatingControl = true, fill = true },
  forwardedRef,
) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { direction, scrollByStep } = useFloatingScrollDirection(scrollRef, contentKey);

  React.useImperativeHandle(forwardedRef, () => scrollRef.current as HTMLDivElement);

  const setScrollRef = React.useCallback((node: HTMLDivElement | null) => {
    (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, []);

  return (
    <div
      className={cn(fill ? 'relative min-h-0 flex-1' : 'relative', containerClassName)}
      style={containerStyle}
      data-scroll-pane-container=""
    >
      <div
        ref={setScrollRef}
        data-scroll-pane=""
        data-scroll-surface=""
        className={cn(
          scrollPaneListClass,
          fill ? 'h-full' : 'max-h-full min-h-0',
          className,
        )}
      >
        {children}
      </div>

      {floatingControl ? (
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center transition-opacity duration-200',
            direction ? 'opacity-100' : 'opacity-0',
          )}
          aria-hidden={!direction}
        >
          <Button
            type="button"
            variant="iconAction"
            size="icon-action"
            className={cn(
              'pointer-events-auto size-10 rounded-full border border-border bg-background/95 shadow-md backdrop-blur-sm',
              !direction && 'pointer-events-none',
            )}
            aria-label={direction === 'down' ? 'Scroll down' : 'Scroll up'}
            onClick={scrollByStep}
          >
            <HugeiconsIcon
              icon={direction === 'down' ? ArrowDown01Icon : ArrowUp01Icon}
              size={20}
              strokeWidth={1.75}
            />
          </Button>
        </div>
      ) : null}
    </div>
  );
});
